/**
 * ManualWeekenderBookingDialog.js
 * 
 * EXTRACTED from AdminWeekendsTab.js (Phase 3A)
 * 
 * A reusable dialog component for creating manual bookings for Weekender events.
 * Preserves existing business logic and API behavior.
 * 
 * Key behavior (Phase 2G model):
 * - customer_name = booking contact (may or may not be attending)
 * - guests[] = actual attendees (must match number_of_guests derived from rooms)
 * - number_of_guests is calculated server-side from room occupancy
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  Plus, 
  Minus, 
  User, 
  Mail, 
  Loader2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../../utils/api';

// ============== HELPER FUNCTIONS ==============

/**
 * Get room occupancy based on room type name.
 * Mirrors backend logic in weekends.py:get_room_occupancy
 */
const getRoomOccupancy = (roomType, roomOptions = []) => {
  // First try to find capacity from room options
  const roomOption = roomOptions.find(r => r.type?.toLowerCase() === roomType?.toLowerCase());
  if (roomOption?.capacity) {
    return roomOption.capacity;
  }
  
  // Fall back to name-based matching (mirrors backend logic)
  const roomTypeLower = (roomType || '').toLowerCase();
  
  // Check for "Single Occupancy" or "Sole Occupancy" variants first
  if (roomTypeLower.includes('single occupancy') || roomTypeLower.includes('sole occupancy')) {
    return 1;
  }
  
  if (roomTypeLower.includes('single') && !roomTypeLower.includes('double') && !roomTypeLower.includes('twin')) {
    return 1;
  } else if (roomTypeLower.includes('double') || roomTypeLower.includes('twin')) {
    return 2;
  } else if (roomTypeLower.includes('triple')) {
    return 3;
  } else if (roomTypeLower.includes('quad') || roomTypeLower.includes('family')) {
    return 4;
  }
  return 2; // Default to 2
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount || 0);
};

// ============== INITIAL FORM STATE ==============

const getInitialFormState = () => ({
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  rooms: {},
  guests: [],
  addOnQuantities: {},
  special_requests: '',
  status: 'confirmed',
  amount_paid: 0,
  discountType: 'none',
  discountValue: '',
  discountReason: ''
});

// ============== MAIN COMPONENT ==============

export default function ManualWeekenderBookingDialog({
  event,
  open,
  onOpenChange,
  authToken,
  onBookingCreated
}) {
  const [formData, setFormData] = useState(getInitialFormState());
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Reset form when dialog opens with a new event
  useEffect(() => {
    if (open && event) {
      setFormData(getInitialFormState());
      setValidationErrors({});
    }
  }, [open, event?.event_id]);

  // Calculate total guests from room selections
  const totalGuests = useMemo(() => {
    if (!event) return 0;
    let total = 0;
    Object.entries(formData.rooms).forEach(([roomType, count]) => {
      total += count * getRoomOccupancy(roomType, event.room_options);
    });
    return total;
  }, [formData.rooms, event]);

  // Synchronize guests array with total guests (room-based)
  useEffect(() => {
    setFormData(prev => {
      const newGuests = [...prev.guests];
      while (newGuests.length < totalGuests) {
        newGuests.push({ first_name: '', last_name: '', email: '' });
      }
      while (newGuests.length > totalGuests) {
        newGuests.pop();
      }
      // Clamp any add-on quantity that now exceeds the reduced guest count
      const clampedAddOns = {};
      Object.entries(prev.addOnQuantities || {}).forEach(([name, qty]) => {
        const c = Math.min(qty, totalGuests);
        if (c > 0) clampedAddOns[name] = c;
      });
      return { ...prev, guests: newGuests, addOnQuantities: clampedAddOns };
    });
  }, [totalGuests]);

  // Calculate booking totals (mirrors backend calculation)
  const totals = useMemo(() => {
    if (!event || totalGuests === 0) {
      return { rooms: 0, addOns: 0, subtotal: 0, deposit: 0 };
    }

    let roomsTotal = 0;
    Object.entries(formData.rooms).forEach(([roomType, count]) => {
      const roomOption = event.room_options?.find(r => r.type === roomType);
      if (roomOption) {
        const occupancy = getRoomOccupancy(roomType, event.room_options);
        const guestsInRoomType = count * occupancy;
        roomsTotal += (roomOption.price_per_person || 0) * guestsInRoomType;
      }
    });

    let addOnsTotal = 0;
    Object.entries(formData.addOnQuantities).forEach(([addOnName, qty]) => {
      const addOn = event.add_ons?.find(a => a.name === addOnName);
      if (addOn && qty > 0) addOnsTotal += (addOn.price || 0) * qty;
    });

    const subtotal = roomsTotal + addOnsTotal;
    const roomDeposit = (event.room_deposit_amount || 50) * totalGuests;

    let addOnsDeposit = 0;
    Object.entries(formData.addOnQuantities).forEach(([addOnName, qty]) => {
      const addOn = event.add_ons?.find(a => a.name === addOnName);
      if (addOn?.deposit_amount && qty > 0) addOnsDeposit += addOn.deposit_amount * qty;
    });

    return {
      rooms: roomsTotal,
      addOns: addOnsTotal,
      subtotal,
      deposit: roomDeposit + addOnsDeposit
    };
  }, [event, formData.rooms, formData.addOnQuantities, totalGuests]);

  // Discount preview (applies to accommodation/rooms subtotal only)
  const discountAmount = useMemo(() => {
    if (formData.discountType === 'none') return 0;
    const val = parseFloat(formData.discountValue) || 0;
    if (val <= 0) return 0;
    let amt = formData.discountType === 'percentage' ? (totals.rooms * val) / 100 : val;
    amt = Math.min(amt, totals.rooms);
    return Math.max(0, Math.round(amt * 100) / 100);
  }, [formData.discountType, formData.discountValue, totals.rooms]);

  const revisedTotal = Math.max(0, Math.round((totals.subtotal - discountAmount) * 100) / 100);
  const revisedDeposit = Math.min(totals.deposit, revisedTotal);

  // Room quantity handlers
  const updateRoomQuantity = useCallback((roomType, delta) => {
    setFormData(prev => {
      const currentQty = prev.rooms[roomType] || 0;
      const newQty = Math.max(0, currentQty + delta);
      const newRooms = { ...prev.rooms };

      if (newQty === 0) {
        delete newRooms[roomType];
      } else {
        newRooms[roomType] = newQty;
      }

      return { ...prev, rooms: newRooms };
    });
  }, []);

  // Guest name handler
  const updateGuestField = useCallback((index, field, value) => {
    setFormData(prev => {
      const newGuests = [...prev.guests];
      if (!newGuests[index]) {
        newGuests[index] = { first_name: '', last_name: '', email: '' };
      }
      newGuests[index][field] = value;
      return { ...prev, guests: newGuests };
    });
  }, []);

  // Add-on quantity handler (clamped 0..totalGuests)
  const setAddOnQuantity = useCallback((addOnName, qty) => {
    setFormData(prev => {
      const clamped = Math.max(0, Math.min(qty, totalGuests));
      const next = { ...prev.addOnQuantities };
      if (clamped <= 0) {
        delete next[addOnName];
      } else {
        next[addOnName] = clamped;
      }
      return { ...prev, addOnQuantities: next };
    });
  }, [totalGuests]);

  // Form validation
  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.customer_name?.trim()) {
      errors.customer_name = 'Customer name is required';
    }
    if (!formData.customer_email?.trim()) {
      errors.customer_email = 'Customer email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      errors.customer_email = 'Invalid email format';
    }
    if (!formData.customer_phone?.trim()) {
      errors.customer_phone = 'Phone number is required';
    }

    if (totalGuests === 0) {
      errors.rooms = 'At least one room must be selected';
    }

    // Validate all guest names are filled
    const missingGuestNames = formData.guests.some(
      (g, idx) => !g.first_name?.trim() || !g.last_name?.trim()
    );
    if (missingGuestNames && totalGuests > 0) {
      errors.guests = 'All guest names must be provided';
    }

    if (formData.discountType !== 'none') {
      const val = parseFloat(formData.discountValue);
      if (!val || val <= 0) {
        errors.discount = 'Enter a discount value greater than 0';
      } else if (formData.discountType === 'percentage' && val > 100) {
        errors.discount = 'Percentage cannot exceed 100%';
      }
      if (!formData.discountReason?.trim()) {
        errors.discountReason = 'A reason is required for the discount';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, totalGuests]);

  // Submit handler
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSaving(true);
    try {
      const roomsArray = Object.entries(formData.rooms)
        .filter(([_, qty]) => qty > 0)
        .map(([room_type, quantity]) => ({ room_type, quantity }));

      const response = await fetch(`${API_URL}/api/weekends/admin/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          event_id: event.event_id,
          customer_name: formData.customer_name.trim(),
          customer_email: formData.customer_email.trim(),
          customer_phone: formData.customer_phone.trim(),
          rooms: roomsArray,
          guests: formData.guests.map(g => ({
            first_name: g.first_name.trim(),
            last_name: g.last_name.trim(),
            email: g.email?.trim() || null
          })),
          selected_add_ons: [],
          add_on_selections: Object.entries(formData.addOnQuantities)
            .filter(([_, qty]) => qty > 0)
            .map(([name, quantity]) => ({ name, quantity })),
          special_requests: formData.special_requests?.trim() || null,
          status: formData.status,
          amount_paid: parseFloat(formData.amount_paid) || 0,
          ...(formData.discountType !== 'none' ? {
            discount_type: formData.discountType,
            discount_value: parseFloat(formData.discountValue) || 0,
            discount_reason: formData.discountReason?.trim() || null
          } : {})
        })
      });

      if (response.ok) {
        const newBooking = await response.json();
        toast.success('Manual booking created successfully');
        setFormData(getInitialFormState());
        onOpenChange(false);
        if (onBookingCreated) {
          onBookingCreated(newBooking);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create booking');
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error('An error occurred while creating the booking');
    } finally {
      setSaving(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Add Manual Booking</DialogTitle>
          <DialogDescription className="text-slate-600">
            Create a booking for {event.title} (e.g., phone/offline bookings)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer Details */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-slate-700">Booking Contact</h3>
            <p className="text-xs text-slate-500">
              The booking contact receives confirmation emails and manages the booking. 
              They may or may not be one of the attendees.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-slate-700">Full Name *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Contact name"
                  className={`bg-white border-slate-300 ${validationErrors.customer_name ? 'border-red-500' : ''}`}
                  data-testid="booking-customer-name"
                />
                {validationErrors.customer_name && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.customer_name}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-700">Email *</Label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="email@example.com"
                  className={`bg-white border-slate-300 ${validationErrors.customer_email ? 'border-red-500' : ''}`}
                  data-testid="booking-customer-email"
                />
                {validationErrors.customer_email && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.customer_email}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-700">Phone *</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="Phone number"
                  className={`bg-white border-slate-300 ${validationErrors.customer_phone ? 'border-red-500' : ''}`}
                  data-testid="booking-customer-phone"
                />
                {validationErrors.customer_phone && (
                  <p className="text-xs text-red-500 mt-1">{validationErrors.customer_phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Room Selection */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-slate-700">Room Selection</h3>
            {validationErrors.rooms && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {validationErrors.rooms}
              </p>
            )}
            <div className="space-y-2">
              {event.room_options?.map((room, idx) => {
                const occupancy = getRoomOccupancy(room.type, event.room_options);
                const count = formData.rooms[room.type] || 0;
                const available = room.available_count;
                const booked = room.booked_count || 0;
                const remaining = available !== null && available !== undefined ? available - booked : null;
                const isSoldOut = remaining !== null && remaining <= 0;

                return (
                  <div 
                    key={idx} 
                    className={`flex items-center justify-between p-3 rounded-lg ${isSoldOut ? 'bg-slate-100 opacity-60' : 'bg-slate-50'}`}
                    data-testid={`room-option-${room.type}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{room.type}</span>
                        {isSoldOut && <Badge variant="destructive" className="text-xs">Sold Out</Badge>}
                        {!isSoldOut && remaining !== null && remaining <= 3 && (
                          <Badge variant="outline" className="text-xs text-orange-600">{remaining} left</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {occupancy} person(s) • {formatCurrency(room.price_per_person)} pp
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateRoomQuantity(room.type, -1)}
                        disabled={count === 0 || isSoldOut}
                        className="h-8 w-8 p-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{count}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateRoomQuantity(room.type, 1)}
                        disabled={isSoldOut || (remaining !== null && count >= remaining)}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Guest Names & Emails */}
          {totalGuests > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-slate-700">
                Attendee Names & Emails ({totalGuests} attendee{totalGuests > 1 ? 's' : ''})
              </h3>
              <p className="text-xs text-slate-500">
                Enter the names of all attendees. The booking contact above is NOT automatically included as an attendee.
                If the contact is attending, include them below.
              </p>
              {validationErrors.guests && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.guests}
                </p>
              )}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {formData.guests.map((guest, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-500" />
                      <Input
                        value={guest.first_name || ''}
                        onChange={(e) => updateGuestField(idx, 'first_name', e.target.value)}
                        placeholder="First name *"
                        className="flex-1"
                        data-testid={`guest-${idx}-firstname`}
                      />
                      <Input
                        value={guest.last_name || ''}
                        onChange={(e) => updateGuestField(idx, 'last_name', e.target.value)}
                        placeholder="Last name *"
                        className="flex-1"
                        data-testid={`guest-${idx}-lastname`}
                      />
                    </div>
                    <div className="flex items-center gap-2 pl-6">
                      <Mail className="w-4 h-4 text-slate-500" />
                      <Input
                        type="email"
                        value={guest.email || ''}
                        onChange={(e) => updateGuestField(idx, 'email', e.target.value)}
                        placeholder="Email (optional - for CC on booking emails)"
                        className="flex-1 text-sm"
                        data-testid={`guest-${idx}-email`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                Attendees with emails will be CC'd on confirmation and payment emails
              </p>
            </div>
          )}

          {/* Add-ons */}
          {event.add_ons?.length > 0 && totalGuests > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-slate-700">Add-ons</h3>
              <p className="text-xs text-slate-500">Select how many passengers need each extra (up to {totalGuests}).</p>
              <div className="space-y-2">
                {event.add_ons.map((addOn, idx) => {
                  const qty = formData.addOnQuantities[addOn.name] || 0;
                  const lineTotal = (addOn.price || 0) * qty;
                  return (
                  <div key={idx} className="p-2 bg-slate-50 rounded" data-testid={`addon-row-${addOn.name}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <span className="text-sm text-slate-800">{addOn.name}</span>
                        <span className="text-violet-600 text-sm ml-1">{formatCurrency(addOn.price)} pp</span>
                        {addOn.deposit_amount > 0 && (
                          <span className="text-xs text-slate-500 block">({formatCurrency(addOn.deposit_amount)} deposit pp)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setAddOnQuantity(addOn.name, qty - 1)}
                          disabled={qty === 0}
                          data-testid={`addon-minus-${addOn.name}`}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="w-8 text-center text-sm font-medium" data-testid={`addon-qty-${addOn.name}`}>{qty}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setAddOnQuantity(addOn.name, qty + 1)}
                          disabled={qty >= totalGuests}
                          data-testid={`addon-plus-${addOn.name}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {qty > 0 && (
                      <p className="text-xs text-slate-500 mt-1">{qty} passenger{qty !== 1 ? 's' : ''} × {formatCurrency(addOn.price)} = {formatCurrency(lineTotal)}</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Special Requests */}
          <div>
            <Label className="text-slate-700">Special Requests</Label>
            <Textarea
              value={formData.special_requests}
              onChange={(e) => setFormData(prev => ({ ...prev, special_requests: e.target.value }))}
              placeholder="Any special requirements..."
              rows={2}
              data-testid="booking-special-requests"
            />
          </div>

          {/* Payment Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
              >
                <SelectTrigger data-testid="booking-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed (Awaiting Payment)</SelectItem>
                  <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                  <SelectItem value="fully_paid">Fully Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700">Amount Paid (£)</Label>
              <Input
                type="number"
                value={formData.amount_paid}
                onChange={(e) => setFormData(prev => ({ ...prev, amount_paid: e.target.value }))}
                min="0"
                step="0.01"
                placeholder="0.00"
                data-testid="booking-amount-paid"
              />
            </div>
          </div>

          {/* Discount */}
          {totalGuests > 0 && (
            <div className="space-y-3 border border-violet-200 bg-violet-50/40 rounded-lg p-4" data-testid="manual-discount-section">
              <h3 className="font-medium text-sm text-slate-700">Discount (optional)</h3>
              <p className="text-xs text-slate-500">
                Applies to the accommodation subtotal only — coach travel and other add-ons are never discounted.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Discount Type</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, discountType: val }))}
                  >
                    <SelectTrigger data-testid="manual-discount-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No discount</SelectItem>
                      <SelectItem value="fixed">Fixed amount (£)</SelectItem>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.discountType !== 'none' && (
                  <div>
                    <Label className="text-slate-700">
                      {formData.discountType === 'percentage' ? 'Value (%)' : 'Value (£)'}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discountValue}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, discountValue: e.target.value }));
                        setValidationErrors(prev => ({ ...prev, discount: undefined }));
                      }}
                      placeholder={formData.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 50'}
                      className={validationErrors.discount ? 'border-red-500' : ''}
                      data-testid="manual-discount-value"
                    />
                  </div>
                )}
              </div>
              {formData.discountType !== 'none' && (
                <div>
                  <Label className="text-slate-700">Reason *</Label>
                  <Input
                    value={formData.discountReason}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, discountReason: e.target.value }));
                      setValidationErrors(prev => ({ ...prev, discountReason: undefined }));
                    }}
                    placeholder="Reason for this discount (required)"
                    className={validationErrors.discountReason ? 'border-red-500' : ''}
                    data-testid="manual-discount-reason"
                  />
                </div>
              )}
              {validationErrors.discount && (
                <p className="text-xs text-red-500">{validationErrors.discount}</p>
              )}
              {validationErrors.discountReason && (
                <p className="text-xs text-red-500">{validationErrors.discountReason}</p>
              )}
            </div>
          )}

          {/* Totals Summary */}
          {totalGuests > 0 && (
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-2 text-slate-700">Booking Summary</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Attendees:</span>
                  <span>{totalGuests}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Rooms Total:</span>
                  <span>{formatCurrency(totals.rooms)}</span>
                </div>
                {totals.addOns > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Add-ons Total:</span>
                    <span>{formatCurrency(totals.addOns)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1 border-t">
                  <span className="text-slate-600">Original Total:</span>
                  <span data-testid="manual-original-total">{formatCurrency(totals.subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-600" data-testid="manual-discount-amount">
                    <span>Discount{formData.discountType === 'percentage' ? ` (${parseFloat(formData.discountValue) || 0}%)` : ''}:</span>
                    <span>−{formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium pt-1 border-t">
                  <span>{discountAmount > 0 ? 'Revised Total:' : 'Total:'}</span>
                  <span data-testid="manual-revised-total">{formatCurrency(revisedTotal)}</span>
                </div>
                <div className="flex justify-between text-violet-600">
                  <span>Deposit Required:</span>
                  <span>{formatCurrency(revisedDeposit)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving || totalGuests === 0}
            data-testid="create-booking-button"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
